import Avatar from '@components/v2/ui/Avatar/Avatar';
import List from '@components/v2/ui/List/List';
import ListItem from '@components/v2/ui/List/ListItem';
import ListItemAvatar from '@components/v2/ui/List/ListItemAvatar';
import ListItemButton from '@components/v2/ui/List/ListItemButton';
import ListItemText from '@components/v2/ui/List/ListItemText';
import Skeleton from '@components/v2/ui/Skeleton/Skeleton';
import Typography from '@components/v2/ui/Typography/Typography';
import { Box, Stack } from '@mui/material';
import type { Ref } from 'react';
import type LangEnum from '../../../../lang/LangEnum';
import { useTranslation } from '../../../translation/TranslationContext';
import type { SelectModalListItem } from './selectModalList.types';

interface SelectModalListProps {
  items: SelectModalListItem[];
  isLoading?: boolean;
  emptyMessage?: LangEnum;
  hasNextPage?: boolean;
  infiniteRef?: Ref<HTMLDivElement>;
}

const AVATAR_SIZE = 32;
const SKELETON_ROWS = [0, 1, 2, 3, 4, 5];

const SelectModalList = ({
  items,
  isLoading = false,
  emptyMessage,
  hasNextPage = false,
  infiniteRef,
}: SelectModalListProps) => {
  const { translate } = useTranslation();

  if (!isLoading && items.length === 0 && emptyMessage) {
    return <Typography color="text.secondary">{translate(emptyMessage)}</Typography>;
  }

  return (
    <List sx={{ mx: -0.75 }}>
      {items.map((item) => (
        <ListItem key={item.id}>
          <ListItemButton sx={{ width: '100%' }} onClick={item.onSelect}>
            <ListItemAvatar size={AVATAR_SIZE}>
              <Avatar
                alt=""
                src={item.avatarSrc}
                initials={item.avatarInitials}
                sx={item.avatarSx}
              />
            </ListItemAvatar>
            <ListItemText
              primary={item.primary}
              secondary={item.secondary}
              slotProps={{ primary: { noWrap: true }, secondary: { noWrap: true } }}
            />
          </ListItemButton>
        </ListItem>
      ))}
      {isLoading &&
        SKELETON_ROWS.map((row) => (
          <ListItem key={`skeleton-${row}`}>
            <Stack direction="row" sx={{ alignItems: 'center', gap: 1, width: '100%', py: 1 }}>
              <Skeleton variant="rounded" width={AVATAR_SIZE} height={AVATAR_SIZE} />
              <Skeleton variant="rounded" height={AVATAR_SIZE} sx={{ flex: 1 }} />
            </Stack>
          </ListItem>
        ))}
      {hasNextPage && infiniteRef && <Box ref={infiniteRef} />}
    </List>
  );
};

export default SelectModalList;
